import { useCallback, useEffect, useRef } from 'react'

import { usePusherEvent } from '~publish/services/pusher'
import type { PusherEvent } from '~publish/services/pusher'

export const DEFAULT_REALTIME_REFETCH_THROTTLE_MS = 30_000

function defaultGetEventChannelId(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined
  if ('channelId' in data && typeof data.channelId === 'string') {
    return data.channelId
  }
  if ('profile_id' in data && typeof data.profile_id === 'string') {
    return data.profile_id
  }
  return undefined
}

export type UseRealtimeRefetchOptions = {
  /** Pusher events that should trigger a refetch. */
  events: PusherEvent[]
  /** The query refetch function — typically `refetch` from Apollo's `useQuery`. */
  refetch: () => unknown | Promise<unknown>
  /**
   * Throttle window in ms. At most one refetch fires per window regardless of
   * event volume; events arriving within the window schedule a single trailing
   * refetch at the end of the window so we don't drop genuine activity.
   */
  throttleMs?: number
  /**
   * Channel IDs that are relevant to the caller. Pusher fires events org-wide,
   * so filtering by the user's connected channels avoids refetches for
   * channels they aren't subscribed to. Leave undefined to skip filtering
   * (e.g. for org-wide signals).
   */
  allowedChannelIds?: string[]
  /**
   * Extracts the channel ID from the event payload. Defaults to picking
   * `channelId` (comment-shaped events) or `profile_id` (post-shaped events).
   */
  getEventChannelId?: (data: unknown) => string | undefined
}

/**
 * Subscribes to Pusher events and triggers a throttled, visibility-gated
 * refetch. Designed for Home modules that show small lists derived from
 * Apollo queries — see CORE-4975.
 *
 * - Leading-edge fire: the first matching event refetches immediately.
 * - Trailing-edge fire: subsequent events within the throttle window are
 *   collapsed into a single refetch at the end of the window.
 * - Visibility gating: while the tab is hidden, events mark the list stale
 *   instead of refetching; one refetch fires on the next visibility change.
 * - Channel filter: when `allowedChannelIds` is provided, events whose
 *   channel ID is not in the set are ignored. Events without a channel ID
 *   are treated as org-wide and pass through.
 */
export function useRealtimeRefetch({
  events,
  refetch,
  throttleMs = DEFAULT_REALTIME_REFETCH_THROTTLE_MS,
  allowedChannelIds,
  getEventChannelId = defaultGetEventChannelId,
}: UseRealtimeRefetchOptions): void {
  const lastRefetchAtRef = useRef(0)
  const trailingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isStaleRef = useRef(false)
  // Capture latest refetch and filter inputs in refs so the Pusher handler
  // stays stable across renders.
  const refetchRef = useRef(refetch)
  const allowedChannelIdsRef = useRef(allowedChannelIds)
  const getEventChannelIdRef = useRef(getEventChannelId)

  useEffect(() => {
    refetchRef.current = refetch
    allowedChannelIdsRef.current = allowedChannelIds
    getEventChannelIdRef.current = getEventChannelId
  }, [refetch, allowedChannelIds, getEventChannelId])

  const performRefetch = useCallback((): void => {
    // A trailing timer scheduled while visible can fire after the tab goes
    // hidden — gate the refetch itself so we honor visibility regardless of
    // when the timer was queued.
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden'
    ) {
      isStaleRef.current = true
      trailingTimerRef.current = null
      return
    }
    lastRefetchAtRef.current = Date.now()
    isStaleRef.current = false
    if (trailingTimerRef.current) {
      clearTimeout(trailingTimerRef.current)
      trailingTimerRef.current = null
    }
    void refetchRef.current()
  }, [])

  const scheduleOrFire = useCallback((): void => {
    const now = Date.now()
    const elapsed = now - lastRefetchAtRef.current
    if (elapsed >= throttleMs) {
      performRefetch()
      return
    }
    if (trailingTimerRef.current) return
    trailingTimerRef.current = setTimeout(performRefetch, throttleMs - elapsed)
  }, [performRefetch, throttleMs])

  usePusherEvent(events, (data: unknown): void => {
    const allowedIds = allowedChannelIdsRef.current
    if (allowedIds) {
      const channelId = getEventChannelIdRef.current(data)
      if (channelId !== undefined && !allowedIds.includes(channelId)) {
        return
      }
    }

    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden'
    ) {
      isStaleRef.current = true
      return
    }

    scheduleOrFire()
  })

  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return
      if (!isStaleRef.current) return
      scheduleOrFire()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return (): void => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [scheduleOrFire])

  useEffect(() => {
    return (): void => {
      if (trailingTimerRef.current) {
        clearTimeout(trailingTimerRef.current)
        trailingTimerRef.current = null
      }
    }
  }, [])
}
